/* eslint-disable @typescript-eslint/no-explicit-any */
import { cloneDeep, get } from 'es-toolkit/compat';

interface ItemWithId {
  id: number | string;
}

export const removeItemFromArray = <T = any>(array: T[], indexToRemove?: number) => {
  const newArray: T[] = array.slice();
  if (indexToRemove === undefined || indexToRemove < 0) {
    return newArray;
  }
  newArray.splice(indexToRemove,
    1);

  return newArray;
};

export const addOrReplaceObjInArr = <T = any>(
  arr: T[],
  obj: T,
  matchKey: string,
) => {
  const clonedArray = cloneDeep(arr);
  const objIndex = clonedArray.findIndex(o => get(o,
    matchKey) === get(obj,
    matchKey));
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  objIndex > -1
    ? clonedArray[objIndex] = obj
    : clonedArray.push(obj);

  return clonedArray;
};

export const indexBy = <T = Record<string, unknown>>(
  array: T[],
  selector: (item: T) => string,
): Record<string, T> => {
  const result = {} as Record<string, T>;
  for (let index = 0, len = array.length; index < len; index++) {
    const item = array[index];
    if (item !== undefined) {
      result[selector(item)] = item;
    }
  }

  return result;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const getItemId = <T extends ItemWithId>(item: T) => item.id.toString();

export const indexById = <T extends ItemWithId>(
  array: T[],
): Record<string, T> => {
  return indexBy(array, getItemId);
};

export const groupBy = <T = Record<string, unknown>>(
  array: T[],
  selector: (item: T) => string,
): Record<string, T[]> => {
  const result: Record<string, T[]> = {};
  array.forEach(item => {
    const value = result[selector(item)];
    if (value) {
      result[selector(item)] = [...value, item];
    } else {
      result[selector(item)] = [item];
    }
  });

  return result;
};

interface SortField<T> {
  ascending: boolean;
  field: keyof T;
  selector?: never;
}

interface SortSelector<T> {
  ascending: boolean;
  field?: never;
  selector: (item: T) => number | string;
}

type SortArgument<T> = SortField<T> | SortSelector<T>;

const getValue = <T>(
  item: T,
  sortArg: SortArgument<T>,
) => {
  if (sortArg.field) {
    return item[sortArg.field];
  }

  if (sortArg.selector) {
    return sortArg.selector(item);
  }

  return '';
};

const getDelta = <T>(
  a: T, b: T, sortArg: SortArgument<T>,
) => {
  const aVal = getValue(a,
    sortArg);
  const bVal = getValue(b,
    sortArg);

  if (aVal === bVal) {
    return 0;
  }

  const delta = aVal > bVal ? 1 : -1;

  return delta * (sortArg.ascending ? 1 : -1);
};

export const sortByMany = <T = any>(arr: T[], sortArgs: SortArgument<T>[]): T[] => {
  const newArray = [...arr];

  return newArray.sort((a, b) => {
    const deltas = sortArgs.map(sortArg => getDelta<T>(
      a,
      b,
      sortArg,
    ));

    const aIndex = newArray.indexOf(a);
    const bIndex = newArray.indexOf(b);

    const indexDelta = aIndex > bIndex ? 1 : -1;

    return deltas.find(Boolean) ?? indexDelta;
  });
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const sortBy = <T = unknown, TValue = unknown>(arr: T[], selector: (item: T) => TValue): T[] => {
  const newArray = [...arr];

  return newArray.sort((a, b) => {
    const aValue = selector(a);
    const bValue = selector(b);
    if (aValue < bValue) {
      return -1;
    } else if (aValue > bValue) {
      return 1;
    } else {
      return 0;
    }
  });
};

export const toArray = <T>(item: T | T[]): T[] => {
  if (Array.isArray(item)) {
    return [...item];
  }

  return [item];
};

export const filterMap = <T, R>(
  array: T[],
  filterFn: (item: T) => boolean,
  mapFn: (item: T) => R,
): R[] => array.reduce<R[]>((acc, item) => filterFn(item) ? [...acc, mapFn(item)] : acc, []);
